import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { 
  Search, 
  Filter, 
  Plus, 
  Download, 
  Pencil, 
  Trash2,
  X,
  RefreshCw,
  Bookmark,
  BookmarkPlus,
  Star,
  Upload,
  History,
  FileText,
  ChevronDown,
  MoreVertical
} from "lucide-react";
import axios from "axios";
import TaskFormModal from "../components/TaskFormModal";
import TaskHistoryModal from "../components/TaskHistoryModal";
import ImportExcelModal from "../components/ImportExcelModal";
import TaskActionMenu from "../components/TaskActionMenu";
import CustomSelect from "../components/CustomSelect";
import SavedFiltersMenu from "../components/SavedFiltersMenu";
import AIAssistant from "../components/AIAssistant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  HIGH: "bg-red-500/10 text-red-500 border-red-500/20",
  MEDIUM: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  LOW: "bg-blue-500/10 text-blue-500 border-blue-500/20"
};

const STATUS_STYLES = {
  ONLINE: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  WIP: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  TODO: "bg-zinc-800 text-zinc-400 border-zinc-700",
  "stand-by": "bg-purple-500/10 text-purple-400 border-purple-500/20",
  Recurring: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20"
};

const getSavedFilters = () => {
  return JSON.parse(localStorage.getItem('savedFilters') || '[]');
};

const saveFilter = (name, filters) => {
  const saved = getSavedFilters();
  const newFilter = { id: Date.now().toString(), name, filters, createdAt: new Date().toISOString() };
  saved.push(newFilter);
  localStorage.setItem('savedFilters', JSON.stringify(saved));
  return saved;
};

const deleteFilter = (filterId) => {
  const saved = getSavedFilters().filter(f => f.id !== filterId);
  localStorage.setItem('savedFilters', JSON.stringify(saved));
  return saved;
};

export default function TaskList() {
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [options, setOptions] = useState({ systems: [], countries: [], priorities: [], statuses: [], projects: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    system: "",
    country: "",
    priority: "",
    status: "",
    search: ""
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [savedFilters, setSavedFilters] = useState(getSavedFilters());
  const [filterName, setFilterName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyTaskId, setHistoryTaskId] = useState(null);
  const [historyTaskInfo, setHistoryTaskInfo] = useState("");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [prefillInfo, setPrefillInfo] = useState("");

  // Handle navigation state (from voice command)
  useEffect(() => {
    if (location.state?.openCreateModal) {
      setEditingTask(null);
      setPrefillInfo(location.state.prefillInfo || "");
      setModalOpen(true);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.system) params.system = filters.system;
      if (filters.country) params.country = filters.country;
      if (filters.priority) params.priority = filters.priority;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;

      const [tasksRes, optionsRes] = await Promise.all([
        axios.get(`${API}/tasks`, { params }),
        axios.get(`${API}/options`)
      ]);
      setTasks(tasksRes.data);
      setOptions(optionsRes.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Errore nel caricamento dei task");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/tasks/export/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tasks_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export completato!");
    } catch (error) {
      toast.error("Errore durante l'export");
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info("Generazione report PDF in corso...");
      const response = await axios.get(`${API}/reports/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TaskCommand_Report_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report PDF scaricato!");
    } catch (error) {
      toast.error("Errore nella generazione del PDF");
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo task?")) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success("Task eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingTask(null);
    setPrefillInfo("");
    setModalOpen(true);
  };

  const handleSave = async (taskData) => {
    try {
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, taskData);
        toast.success("Task aggiornato");
      } else {
        await axios.post(`${API}/tasks`, taskData);
        toast.success("Task creato");
      }
      setModalOpen(false);
      setPrefillInfo("");
      fetchData();
    } catch (error) {
      toast.error("Errore durante il salvataggio");
    }
  };

  const clearFilters = () => {
    setFilters({
      system: "",
      country: "",
      priority: "",
      status: "",
      search: ""
    });
  };

  // Voice command handlers
  const handleVoiceSearch = (term) => {
    setFilters(prev => ({ ...prev, search: term }));
    setShowFilters(true);
  };

  const handleVoiceFilterPriority = (priority) => {
    setFilters(prev => ({ ...prev, priority }));
    setShowFilters(true);
  };

  const handleVoiceFilterStatus = (status) => {
    setFilters(prev => ({ ...prev, status }));
    setShowFilters(true);
  };

  const handleVoiceCreateTask = (info) => {
    setEditingTask(null);
    setPrefillInfo(info);
    setModalOpen(true);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== "");

  const getStatusStyle = (status) => {
    if (status?.includes("Recurring")) return STATUS_STYLES["Recurring"];
    return STATUS_STYLES[status] || STATUS_STYLES["TODO"];
  };

  const handleSaveFilter = () => {
    if (!filterName.trim()) {
      toast.error("Inserisci un nome per il filtro");
      return;
    }
    if (!hasActiveFilters) {
      toast.error("Seleziona almeno un filtro da salvare");
      return;
    }
    const updated = saveFilter(filterName.trim(), filters);
    setSavedFilters(updated);
    setFilterName("");
    setShowSaveDialog(false);
    toast.success("Filtro salvato!");
  };

  const handleApplyFilter = (savedFilter) => {
    setFilters(savedFilter.filters);
    toast.success(`Filtro "${savedFilter.name}" applicato`);
  };

  const handleDeleteFilter = (filterId, e) => {
    e.stopPropagation();
    const updated = deleteFilter(filterId);
    setSavedFilters(updated);
    toast.success("Filtro eliminato");
  };

  const handleShowHistory = (task) => {
    setHistoryTaskId(task.id);
    setHistoryTaskInfo(task.info);
    setHistoryModalOpen(true);
  };

  // Mobile Task Card Component
  const TaskCard = ({ task }) => (
    <div 
      className="p-4 bg-card border border-border rounded-lg space-y-3"
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{task.info || "—"}</p>
          {task.project && (
            <p className="text-xs text-muted-foreground">{task.project}</p>
          )}
        </div>
        <TaskActionMenu
          task={task}
          onEdit={handleEdit}
          onHistory={handleShowHistory}
          onDelete={handleDelete}
        />
      </div>
      
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="font-mono text-xs">
          {task.system}
        </Badge>
        <span className="text-xs text-muted-foreground">{task.country}</span>
        <Badge variant="outline" className={`text-xs ${PRIORITY_STYLES[task.priority]}`}>
          {task.priority}
        </Badge>
        <Badge variant="outline" className={`text-xs ${getStatusStyle(task.status)}`}>
          {task.status}
        </Badge>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Progress value={task.completion * 100} className="w-20 h-2" />
          <span className="text-xs text-muted-foreground">
            {Math.round(task.completion * 100)}%
          </span>
        </div>
        {task.deadline && (
          <span className="text-xs text-muted-foreground">
            {new Date(task.deadline).toLocaleDateString('it-IT')}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6" data-testid="task-list">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Lista Task</h1>
            <p className="text-sm text-muted-foreground mt-1">{tasks.length} task trovati</p>
          </div>
          <Button onClick={handleCreate} size="sm" className="md:hidden" data-testid="create-task-btn-mobile">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Desktop action buttons */}
        <div className="hidden md:flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-2" />
            Report PDF
          </Button>
          <Button variant="outline" onClick={() => setImportModalOpen(true)} data-testid="import-btn">
            <Upload className="w-4 h-4 mr-2" />
            Import Excel
          </Button>
          <Button variant="outline" onClick={handleExport} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button onClick={handleCreate} data-testid="create-task-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Task
          </Button>
        </div>

        {/* Mobile action buttons */}
        <div className="flex md:hidden gap-2 overflow-x-auto pb-2">
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText className="w-4 h-4 mr-1" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImportModalOpen(true)}>
            <Upload className="w-4 h-4 mr-1" />
            Import
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-1" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-border" data-testid="filters-card">
        <CardHeader className="p-4 pb-3">
          <div className="flex items-center justify-between">
            <button 
              className="flex items-center gap-2 md:cursor-default"
              onClick={() => setShowFilters(!showFilters)}
            >
              <CardTitle className="text-base md:text-lg flex items-center gap-2">
                <Filter className="w-4 h-4 md:w-5 md:h-5" />
                Filtri
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-1">{Object.values(filters).filter(v => v).length}</Badge>
                )}
              </CardTitle>
              <ChevronDown className={`w-4 h-4 md:hidden transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            <div className="flex items-center gap-2">
              {/* AI Assistant (Voice + Chat) */}
              <AIAssistant
                onSearch={handleVoiceSearch}
                onFilterPriority={handleVoiceFilterPriority}
                onFilterStatus={handleVoiceFilterStatus}
                onClearFilters={clearFilters}
                onCreateTask={handleVoiceCreateTask}
                onExportPdf={handleExportPDF}
              />
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2">
                  <X className="w-3 h-3 mr-1" />
                  <span className="hidden sm:inline">Reset</span>
                </Button>
              )}
              <SavedFiltersMenu
                filters={savedFilters}
                onApply={handleApplyFilter}
                onDelete={handleDeleteFilter}
              />
              {hasActiveFilters && (
                showSaveDialog ? (
                  <div className="hidden sm:flex items-center gap-2">
                    <Input
                      placeholder="Nome filtro..."
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                      className="h-8 w-32 bg-secondary text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveFilter()}
                    />
                    <Button size="sm" className="h-8" onClick={handleSaveFilter}>
                      Salva
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setShowSaveDialog(false)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-8 hidden sm:flex"
                    onClick={() => setShowSaveDialog(true)}
                  >
                    <BookmarkPlus className="w-4 h-4 mr-2" />
                    Salva
                  </Button>
                )
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className={`p-4 pt-0 ${showFilters ? 'block' : 'hidden md:block'}`}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
            <div className="col-span-2 md:col-span-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 bg-secondary h-9 md:h-10 text-sm"
                data-testid="search-input"
              />
            </div>

            <CustomSelect
              value={filters.system || "all"}
              onValueChange={(v) => setFilters({ ...filters, system: v === "all" ? "" : v })}
              placeholder="Tutti i sistemi"
              options={[
                { value: "all", label: "Tutti i sistemi" },
                ...options.systems.map(s => ({ value: s, label: s }))
              ]}
              triggerClassName="bg-secondary h-9 md:h-10 text-sm"
            />

            <CustomSelect
              value={filters.country || "all"}
              onValueChange={(v) => setFilters({ ...filters, country: v === "all" ? "" : v })}
              placeholder="Tutti i paesi"
              options={[
                { value: "all", label: "Tutti i paesi" },
                ...options.countries.map(c => ({ value: c, label: c }))
              ]}
              triggerClassName="bg-secondary h-9 md:h-10 text-sm"
            />

            <CustomSelect
              value={filters.priority || "all"}
              onValueChange={(v) => setFilters({ ...filters, priority: v === "all" ? "" : v })}
              placeholder="Tutte le priorità"
              options={[
                { value: "all", label: "Tutte le priorità" },
                ...options.priorities.map(p => ({ value: p, label: p }))
              ]}
              triggerClassName="bg-secondary h-9 md:h-10 text-sm"
            />

            <CustomSelect
              value={filters.status || "all"}
              onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}
              placeholder="Tutti gli status"
              options={[
                { value: "all", label: "Tutti gli status" },
                ...options.statuses.map(s => ({ value: s, label: s }))
              ]}
              triggerClassName="bg-secondary h-9 md:h-10 text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Task List - Desktop Table */}
      <Card className="border-border hidden md:block" data-testid="tasks-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <p>Nessun task trovato</p>
              <Button variant="outline" className="mt-4" onClick={handleCreate}>
                Crea il primo task
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="w-[250px]">Info</TableHead>
                  <TableHead>Sistema</TableHead>
                  <TableHead>Paese</TableHead>
                  <TableHead>Priorità</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Completamento</TableHead>
                  <TableHead>Deadline</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow 
                    key={task.id} 
                    className="border-border hover:bg-secondary/50"
                    data-testid={`task-row-${task.id}`}
                  >
                    <TableCell className="font-medium">
                      <div>
                        <p className="truncate max-w-[230px]">{task.info || "—"}</p>
                        {task.project && (
                          <p className="text-xs text-muted-foreground">{task.project}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {task.system}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{task.country}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={PRIORITY_STYLES[task.priority]}>
                        {task.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getStatusStyle(task.status)}>
                        {task.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={task.completion * 100} className="w-16 h-2" />
                        <span className="text-xs text-muted-foreground w-10">
                          {Math.round(task.completion * 100)}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {task.deadline ? (
                        <span className="text-sm">
                          {new Date(task.deadline).toLocaleDateString('it-IT')}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <TaskActionMenu
                        task={task}
                        onEdit={handleEdit}
                        onHistory={handleShowHistory}
                        onDelete={handleDelete}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Task List - Mobile Cards */}
      <div className="md:hidden space-y-3" data-testid="tasks-cards">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
            <p>Nessun task trovato</p>
            <Button variant="outline" className="mt-4" onClick={handleCreate}>
              Crea il primo task
            </Button>
          </div>
        ) : (
          tasks.map((task) => (
            <TaskCard key={task.id} task={task} />
          ))
        )}
      </div>

      <TaskFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setPrefillInfo("");
        }}
        onSave={handleSave}
        task={editingTask}
        options={options}
        prefillInfo={prefillInfo}
      />

      <TaskHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        taskId={historyTaskId}
        taskInfo={historyTaskInfo}
      />

      <ImportExcelModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportComplete={fetchData}
      />
    </div>
  );
}
